"use client";

import { c as _c } from "react/compiler-runtime";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import CommonDropdownItemsMobileWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsMobileWrapper";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import { DropLocation } from "@/components/waves/drops/Drop";
import { AdditionalActionPromiseBadge } from "@/components/waves/drops/AdditionalActionPromiseBadge";
import WaveDropActionsOpen from "@/components/waves/drops/WaveDropActionsOpen";
import WaveDropActionsOptions from "@/components/waves/drops/WaveDropActionsOptions";
import WaveDropMobileMenuDelete from "@/components/waves/drops/WaveDropMobileMenuDelete";
import WaveDropMobileMenuCopyLink from "@/components/waves/drops/WaveDropMobileMenuCopyLink";
import WaveDropMobileMenuOpen from "@/components/waves/drops/WaveDropMobileMenuOpen";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { MemesArtResubmitAction } from "@/components/waves/memes/submission/MemesArtResubmitAction";
import ParticipationDropVoteDetailsTrigger from "@/components/waves/drops/participation/ratings/ParticipationDropVoteDetailsTrigger";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ImageScale } from "@/helpers/image.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { startDropOpen } from "@/utils/monitoring/dropOpenTiming";
import Link from "next/link";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import MemeDropTraits from "./MemeDropTraits";
import MemesLeaderboardDropArtistInfo from "./MemesLeaderboardDropArtistInfo";
import MemesLeaderboardDropCard from "./MemesLeaderboardDropCard";
import MemesLeaderboardDropDescription from "./MemesLeaderboardDropDescription";
import MemesLeaderboardDropHeader from "./MemesLeaderboardDropHeader";
import MemesLeaderboardDropVoteSummary from "./MemesLeaderboardDropVoteSummary";
interface MemesLeaderboardDropProps {
  readonly drop: ExtendedDrop;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly wave?: ApiWave | undefined;
  readonly location?: DropLocation | undefined;
  readonly onSourceDropDeleted?: (() => void) | undefined;
}
const getNonEmptyText = (value: string | null | undefined): string | null => {
  const text = value?.trim();
  return text && text.length > 0 ? text : null;
};
const getMetadataValue = (drop: ExtendedDrop, dataKey: string): string | null => getNonEmptyText(drop.metadata.find(metadata => metadata.data_key === dataKey)?.data_value);
export const MemesLeaderboardDrop: React.FC<MemesLeaderboardDropProps> = t0 => {
  const $ = _c(150);
  const {
    drop,
    onDropClick,
    wave,
    location: t1,
    onSourceDropDeleted
  } = t0;
  const location = t1 === undefined ? DropLocation.WAVE : t1;
  const isMobileScreen = useIsMobileScreen();
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const {
    canDelete
  } = useDropInteractionRules(drop);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  const [isResubmitModalOpen, setIsResubmitModalOpen] = useState(false);
  const openResubmitAfterMenuCloseRef = useRef(false);
  const {
    hasTouchScreen
  } = useDeviceInfo();
  let mediaImageScale = ImageScale.AUTOx800;
  if (isMobileScreen) {
    mediaImageScale = ImageScale.AUTOx450;
  } else {
    if (isTabletOrSmaller) {
      mediaImageScale = ImageScale.AUTOx600;
    }
  }
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      openResubmitAfterMenuCloseRef.current = false;
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const clearDeferredResubmitOpen = t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      clearDeferredResubmitOpen();
    };
    t4 = [clearDeferredResubmitOpen];
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] !== hasTouchScreen) {
    t5 = {
      hasTouchScreen,
      onInteractionStart: clearDeferredResubmitOpen
    };
    $[3] = hasTouchScreen;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    isActive,
    setIsActive,
    touchHandlers
  } = useLongPressInteraction(t5);
  let t6;
  if ($[5] !== setIsActive) {
    t6 = () => {
      openResubmitAfterMenuCloseRef.current = true;
      setIsActive(false);
    };
    $[5] = setIsActive;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const openResubmitAfterMobileMenuCloses = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (!openResubmitAfterMenuCloseRef.current) {
        return;
      }
      clearDeferredResubmitOpen();
      setIsResubmitModalOpen(true);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const handleMobileMenuAfterLeave = t7;
  let description;
  let t8;
  let title;
  if ($[8] !== drop) {
    const firstPart = drop.parts.at(0);
    title = getNonEmptyText(drop.title) ?? getMetadataValue(drop, "title") ?? "Artwork Title";
    description = getNonEmptyText(firstPart?.content) ?? getMetadataValue(drop, "description") ?? "This is an artwork submission for The Memes collection.";
    t8 = firstPart?.media.at(0);
    $[8] = drop;
    $[9] = description;
    $[10] = t8;
    $[11] = title;
  } else {
    description = $[9];
    t8 = $[10];
    title = $[11];
  }
  const artworkMedia = t8;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t9;
  if ($[12] !== artworkMedia || $[13] !== canDelete || $[14] !== description || $[15] !== drop || $[16] !== hasTouchScreen || $[17] !== isMobileScreen || $[18] !== location || $[19] !== mediaImageScale || $[20] !== onDropClick || $[21] !== onSourceDropDeleted || $[22] !== title || $[23] !== touchHandlers || $[24] !== wave) {
    const firstThreeVoters = drop.top_raters.slice(0, 3);
    t24 = "tw-w-full tw-cursor-pointer tw-@container";
    if ($[43] !== drop || $[44] !== hasTouchScreen || $[45] !== isMobileScreen || $[46] !== onDropClick) {
      t25 = () => {
        if (hasTouchScreen) {
          return;
        }
        startDropOpen({
          dropId: drop.id,
          waveId: drop.wave.id,
          source: "leaderboard_memes",
          isMobile: isMobileScreen
        });
        onDropClick(drop);
      };
      $[43] = drop;
      $[44] = hasTouchScreen;
      $[45] = isMobileScreen;
      $[46] = onDropClick;
      $[47] = t25;
    } else {
      t25 = $[47];
    }
    t23 = "tw-group tw-w-full";
    t22 = touchHandlers;
    T0 = MemesLeaderboardDropCard;
    t21 = drop;
    let t26;
    if ($[48] !== drop) {
      t26 = <MemesLeaderboardDropArtistInfo drop={drop} />;
      $[48] = drop;
      $[49] = t26;
    } else {
      t26 = $[49];
    }
    let t27;
    if ($[50] !== canDelete || $[51] !== drop || $[52] !== hasTouchScreen || $[53] !== onSourceDropDeleted || $[54] !== wave) {
      t27 = !hasTouchScreen && <><WaveDropActionsOpen drop={drop} /><MemesArtResubmitAction drop={drop} wave={wave} onSourceDropDeleted={onSourceDropDeleted} />{canDelete && <WaveDropActionsOptions drop={drop} />}</>;
      $[50] = canDelete;
      $[51] = drop;
      $[52] = hasTouchScreen;
      $[53] = onSourceDropDeleted;
      $[54] = wave;
      $[55] = t27;
    } else {
      t27 = $[55];
    }
    let t28;
    if ($[56] !== t27) {
      t28 = <div className="tw-flex tw-gap-2 tw-text-iron-400">{t27}</div>;
      $[56] = t27;
      $[57] = t28;
    } else {
      t28 = $[57];
    }
    if ($[58] !== t26 || $[59] !== t28) {
      t18 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 tw-pb-3"><div className="tw-flex tw-items-start tw-justify-between tw-gap-4">{t26}{t28}</div></div>;
      $[58] = t26;
      $[59] = t28;
      $[60] = t18;
    } else {
      t18 = $[60];
    }
    const t29 = artworkMedia?.mime_type;
    let t30;
    if ($[61] !== drop.id || $[62] !== t29) {
      t30 = <MediaTypeBadge mimeType={t29} dropId={drop.id} size="sm" />;
      $[61] = drop.id;
      $[62] = t29;
      $[63] = t30;
    } else {
      t30 = $[63];
    }
    let t31;
    if ($[64] !== title) {
      t31 = <MemesLeaderboardDropHeader title={title} />;
      $[64] = title;
      $[65] = t31;
    } else {
      t31 = $[65];
    }
    let t32;
    if ($[66] !== drop.is_additional_action_promised) {
      t32 = drop.is_additional_action_promised === true && <AdditionalActionPromiseBadge />;
      $[66] = drop.is_additional_action_promised;
      $[67] = t32;
    } else {
      t32 = $[67];
    }
    let t33;
    if ($[68] !== t30 || $[69] !== t31 || $[70] !== t32) {
      t33 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">{t30}{t31}{t32}</div>;
      $[68] = t30;
      $[69] = t31;
      $[70] = t32;
      $[71] = t33;
    } else {
      t33 = $[71];
    }
    let t34;
    if ($[72] !== description) {
      t34 = <MemesLeaderboardDropDescription description={description} />;
      $[72] = description;
      $[73] = t34;
    } else {
      t34 = $[73];
    }
    if ($[74] !== t33 || $[75] !== t34) {
      t19 = <div className="tw-px-4 tw-pb-4 tw-pt-4"><div className="tw-space-y-1">{t33}{t34}</div></div>;
      $[74] = t33;
      $[75] = t34;
      $[76] = t19;
    } else {
      t19 = $[76];
    }
    if ($[77] !== artworkMedia || $[78] !== location || $[79] !== mediaImageScale) {
      t20 = artworkMedia && <div className={`tw-flex tw-h-96 tw-justify-center ${location === DropLocation.WAVE ? "tw-bg-iron-950" : "tw-bg-iron-900/40"}`}><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} imageScale={mediaImageScale} /></div>;
      $[77] = artworkMedia;
      $[78] = location;
      $[79] = mediaImageScale;
      $[80] = t20;
    } else {
      t20 = $[80];
    }
    t16 = "tw-mt-4 tw-flex tw-flex-col tw-gap-y-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4";
    if ($[81] !== drop) {
      t17 = <MemeDropTraits drop={drop} />;
      $[81] = drop;
      $[82] = t17;
    } else {
      t17 = $[82];
    }
    t14 = "tw-flex tw-flex-col tw-justify-between tw-gap-4 @[700px]:tw-flex-row @[700px]:tw-items-center";
    if ($[83] !== drop) {
      t15 = <MemesLeaderboardDropVoteSummary drop={drop} />;
      $[83] = drop;
      $[84] = t15;
    } else {
      t15 = $[84];
    }
    t12 = "tw-flex tw-w-full tw-flex-shrink-0 tw-items-center tw-justify-between tw-gap-4 @[700px]:tw-w-auto @[700px]:tw-justify-end";
    t13 = _temp;
    t11 = "tw-flex tw-items-center tw-gap-2 @[700px]:tw-hidden";
    t9 = "tw-flex tw-items-center -tw-space-x-2";
    t10 = firstThreeVoters.map(_temp3);
    $[12] = artworkMedia;
    $[13] = canDelete;
    $[14] = description;
    $[15] = drop;
    $[16] = hasTouchScreen;
    $[17] = isMobileScreen;
    $[18] = location;
    $[19] = mediaImageScale;
    $[20] = onDropClick;
    $[21] = onSourceDropDeleted;
    $[22] = title;
    $[23] = touchHandlers;
    $[24] = wave;
    $[25] = T0;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
    $[39] = t23;
    $[40] = t24;
    $[41] = t25;
    $[42] = t9;
  } else {
    T0 = $[25];
    t10 = $[26];
    t11 = $[27];
    t12 = $[28];
    t13 = $[29];
    t14 = $[30];
    t15 = $[31];
    t16 = $[32];
    t17 = $[33];
    t18 = $[34];
    t19 = $[35];
    t20 = $[36];
    t21 = $[37];
    t22 = $[38];
    t23 = $[39];
    t24 = $[40];
    t25 = $[41];
    t9 = $[42];
  }
  let t26;
  if ($[85] !== t10 || $[86] !== t9) {
    t26 = <div className={t9}>{t10}</div>;
    $[85] = t10;
    $[86] = t9;
    $[87] = t26;
  } else {
    t26 = $[87];
  }
  let t27;
  if ($[88] !== drop) {
    t27 = <ParticipationDropVoteDetailsTrigger drop={drop} density="compact" />;
    $[88] = drop;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  let t28;
  if ($[90] !== t11 || $[91] !== t26 || $[92] !== t27) {
    t28 = <div className={t11}>{t26}{t27}</div>;
    $[90] = t11;
    $[91] = t26;
    $[92] = t27;
    $[93] = t28;
  } else {
    t28 = $[93];
  }
  let t29;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setIsVotingModalOpen(true);
    $[94] = t29;
  } else {
    t29 = $[94];
  }
  let t30;
  if ($[95] !== drop) {
    t30 = <VotingModalButton drop={drop} onClick={t29} />;
    $[95] = drop;
    $[96] = t30;
  } else {
    t30 = $[96];
  }
  let t31;
  if ($[97] !== t12 || $[98] !== t13 || $[99] !== t28 || $[100] !== t30) {
    t31 = <div className={t12} onClick={t13}>{t28}{t30}</div>;
    $[97] = t12;
    $[98] = t13;
    $[99] = t28;
    $[100] = t30;
    $[101] = t31;
  } else {
    t31 = $[101];
  }
  let t32;
  if ($[102] !== t14 || $[103] !== t15 || $[104] !== t31) {
    t32 = <div className={t14}>{t15}{t31}</div>;
    $[102] = t14;
    $[103] = t15;
    $[104] = t31;
    $[105] = t32;
  } else {
    t32 = $[105];
  }
  let t33;
  if ($[106] !== t16 || $[107] !== t17 || $[108] !== t32) {
    t33 = <div className={t16}>{t17}{t32}</div>;
    $[106] = t16;
    $[107] = t17;
    $[108] = t32;
    $[109] = t33;
  } else {
    t33 = $[109];
  }
  let t34;
  if ($[110] !== t18 || $[111] !== t19 || $[112] !== t20 || $[113] !== t33) {
    t34 = <div>{t18}{t19}{t20}{t33}</div>;
    $[110] = t18;
    $[111] = t19;
    $[112] = t20;
    $[113] = t33;
    $[114] = t34;
  } else {
    t34 = $[114];
  }
  let t35;
  if ($[115] !== drop || $[116] !== isMobileScreen || $[117] !== isVotingModalOpen) {
    t35 = isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />;
    $[115] = drop;
    $[116] = isMobileScreen;
    $[117] = isVotingModalOpen;
    $[118] = t35;
  } else {
    t35 = $[118];
  }
  let t36;
  if ($[119] !== T0 || $[120] !== t21 || $[121] !== t34 || $[122] !== t35) {
    t36 = <T0 drop={t21}>{t34}{t35}</T0>;
    $[119] = T0;
    $[120] = t21;
    $[121] = t34;
    $[122] = t35;
    $[123] = t36;
  } else {
    t36 = $[123];
  }
  let t37;
  if ($[124] !== t22 || $[125] !== t36) {
    t37 = <div {...t22}>{t36}</div>;
    $[124] = t22;
    $[125] = t36;
    $[126] = t37;
  } else {
    t37 = $[126];
  }
  let t38;
  if ($[127] !== canDelete || $[128] !== drop || $[129] !== hasTouchScreen || $[130] !== isActive || $[131] !== onSourceDropDeleted || $[132] !== openResubmitAfterMobileMenuCloses || $[133] !== setIsActive || $[134] !== wave) {
    t38 = hasTouchScreen && createPortal(<CommonDropdownItemsMobileWrapper isOpen={isActive} setOpen={setIsActive} onAfterLeave={handleMobileMenuAfterLeave}><div className="tw-grid tw-grid-cols-1 tw-gap-y-2"><WaveDropMobileMenuOpen drop={drop} onOpenChange={() => setIsActive(false)} /><WaveDropMobileMenuCopyLink drop={drop} onCopy={() => setIsActive(false)} /><MemesArtResubmitAction drop={drop} wave={wave} variant="menu" onOpenModal={openResubmitAfterMobileMenuCloses} onSourceDropDeleted={onSourceDropDeleted} />{canDelete && <WaveDropMobileMenuDelete drop={drop} onDropDeleted={() => setIsActive(false)} />}</div></CommonDropdownItemsMobileWrapper>, document.body);
    $[127] = canDelete;
    $[128] = drop;
    $[129] = hasTouchScreen;
    $[130] = isActive;
    $[131] = onSourceDropDeleted;
    $[132] = openResubmitAfterMobileMenuCloses;
    $[133] = setIsActive;
    $[134] = wave;
    $[135] = t38;
  } else {
    t38 = $[135];
  }
  let t39;
  if ($[136] !== drop || $[137] !== isResubmitModalOpen || $[138] !== onSourceDropDeleted || $[139] !== wave) {
    t39 = wave && isResubmitModalOpen && <MemesArtSubmissionModal isOpen={isResubmitModalOpen} wave={wave} sourceDrop={drop} onClose={() => setIsResubmitModalOpen(false)} onSourceDropDeleted={onSourceDropDeleted} />;
    $[136] = drop;
    $[137] = isResubmitModalOpen;
    $[138] = onSourceDropDeleted;
    $[139] = wave;
    $[140] = t39;
  } else {
    t39 = $[140];
  }
  let t40;
  if ($[141] !== t23 || $[142] !== t37 || $[143] !== t38 || $[144] !== t39) {
    t40 = <div className={t23}>{t37}{t38}{t39}</div>;
    $[141] = t23;
    $[142] = t37;
    $[143] = t38;
    $[144] = t39;
    $[145] = t40;
  } else {
    t40 = $[145];
  }
  let t41;
  if ($[146] !== t24 || $[147] !== t25 || $[148] !== t40) {
    t41 = <div className={t24} onClick={t25}>{t40}</div>;
    $[146] = t24;
    $[147] = t25;
    $[148] = t40;
    $[149] = t41;
  } else {
    t41 = $[149];
  }
  return t41;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3(voter) {
  return <Link key={voter.profile.handle ?? voter.profile.primary_address} href={`/${voter.profile.handle ?? voter.profile.primary_address}`} onClick={_temp2}>{voter.profile.pfp ? <img className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800 tw-object-contain" src={voter.profile.pfp} alt="Recent voter" /> : <div className="tw-h-6 tw-w-6 tw-rounded-lg tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800" />}</Link>;
}